// Klant-historie: alle eerder verzonden Verfkeuze-mails per klant (klantnummer of email).
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface CustomerHistoryEntry {
  id: string;
  orderid: number;
  ordernumber: string | null;
  template_key: string;
  subject: string;
  recipient: string;
  sent_at: string;
  sent_by: string | null;
  customer_number: string | null;
  customer_email: string | null;
}

export const useCustomerHistory = (
  customerNumber: string | null | undefined,
  customerEmail?: string | null | undefined,
) => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();

  const normalizedNumber =
    typeof customerNumber === "string" && customerNumber.trim().length > 0
      ? customerNumber.trim()
      : null;
  const normalizedEmail =
    typeof customerEmail === "string" && customerEmail.trim().length > 0
      ? customerEmail.trim().toLowerCase()
      : null;

  const enabled =
    isAuthenticated &&
    isSessionValid &&
    !isValidating &&
    (normalizedNumber !== null || normalizedEmail !== null);

  return useQuery({
    queryKey: ["customer-history", normalizedNumber, normalizedEmail] as const,
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerHistoryEntry[]> => {
      const orConditions: string[] = [];
      if (normalizedNumber) orConditions.push(`customer_number.eq.${normalizedNumber}`);
      if (normalizedEmail) orConditions.push(`customer_email.ilike.${normalizedEmail}`);
      if (orConditions.length === 0) return [];

      const { data, error } = await supabase
        .from("customer_notification_log")
        .select(
          "id, orderid, template_key, subject, recipient, sent_at, sent_by, customer_number, customer_email",
        )
        .or(orConditions.join(","))
        .order("sent_at", { ascending: false })
        .limit(200);
      if (error) throw error;

      const rows = (data ?? []) as Omit<CustomerHistoryEntry, "ordernumber">[];
      const orderids = Array.from(new Set(rows.map((r) => r.orderid).filter((n) => typeof n === "number")));

      let orderMap = new Map<number, string>();
      if (orderids.length > 0) {
        const { data: orderRows } = await supabase
          .from("orders_live_ls")
          .select("orderid, ordernumber")
          .in("orderid", orderids);
        (orderRows ?? []).forEach((o: any) => {
          if (typeof o.orderid === "number") orderMap.set(o.orderid, o.ordernumber ?? null);
        });
      }

      return rows.map((r) => ({
        ...r,
        ordernumber: orderMap.get(r.orderid) ?? null,
      }));
    },
  });
};
